import React, { useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function AlertSettings({ token, onClose }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/alert-thresholds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    }
    setLoading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const formData = new FormData();
      Object.entries(settings).forEach(([key, value]) => {
        if (key !== 'type' && key !== 'updated_at' && key !== 'updated_by') {
          formData.append(key, value);
        }
      });

      const response = await fetch(`${BACKEND_URL}/api/settings/alert-thresholds`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Settings saved successfully' });
        setTimeout(() => {
          if (onClose) onClose();
        }, 1500);
      } else {
        const data = await response.json();
        setMessage({ type: 'error', text: data.detail || 'Failed to save settings' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    setSaving(false);
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading settings...</div>;

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    fontSize: '1rem',
    background: 'white'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#1E293B',
    fontSize: '0.875rem'
  };

  const sectionStyle = {
    background: '#F8FAFC',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem'
  };

  const checkboxStyle = {
    width: '20px',
    height: '20px',
    marginRight: '0.75rem',
    cursor: 'pointer'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '16px 16px 0 0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>⚙️ Alert Settings</h2>
            <button 
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '1.25rem'
              }}
            >
              ×
            </button>
          </div>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.875rem' }}>
            Configure when alerts are triggered on the dashboard
          </p>
        </div>

        {message && (
          <div style={{
            padding: '1rem',
            margin: '1rem',
            borderRadius: '8px',
            background: message.type === 'success' ? '#D1FAE5' : '#FEE2E2',
            color: message.type === 'success' ? '#065F46' : '#991B1B'
          }}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSave} style={{ padding: '1.5rem' }}>
          {/* Pending Approval Alerts */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="enable_pending"
                checked={settings?.enable_pending_approval_alerts ?? true}
                onChange={(e) => updateSetting('enable_pending_approval_alerts', e.target.checked)}
                style={checkboxStyle}
              />
              <label htmlFor="enable_pending" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                ⏰ Pending Approval Alerts
              </label>
            </div>
            
            {settings?.enable_pending_approval_alerts && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Medium alert after (days)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={settings?.pending_approval_days_medium ?? 3}
                    onChange={(e) => updateSetting('pending_approval_days_medium', parseInt(e.target.value))}
                    style={inputStyle}
                    data-testid="pending-medium-days"
                  />
                </div>
                <div>
                  <label style={labelStyle}>High alert after (days)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={settings?.pending_approval_days_high ?? 7}
                    onChange={(e) => updateSetting('pending_approval_days_high', parseInt(e.target.value))}
                    style={inputStyle}
                    data-testid="pending-high-days"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Long Repair Alerts */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="enable_repair"
                checked={settings?.enable_long_repair_alerts ?? true}
                onChange={(e) => updateSetting('enable_long_repair_alerts', e.target.checked)}
                style={checkboxStyle}
              />
              <label htmlFor="enable_repair" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                🔧 Long Repair Alerts
              </label>
            </div>
            
            {settings?.enable_long_repair_alerts && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Medium alert after (days)</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={settings?.long_repair_days_medium ?? 5}
                    onChange={(e) => updateSetting('long_repair_days_medium', parseInt(e.target.value))}
                    style={inputStyle}
                    data-testid="repair-medium-days"
                  />
                </div>
                <div>
                  <label style={labelStyle}>High alert after (days)</label>
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={settings?.long_repair_days_high ?? 10}
                    onChange={(e) => updateSetting('long_repair_days_high', parseInt(e.target.value))}
                    style={inputStyle}
                    data-testid="repair-high-days"
                  />
                </div>
              </div>
            )}
          </div>

          {/* High Cost Alerts */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="enable_cost"
                checked={settings?.enable_high_cost_alerts ?? true}
                onChange={(e) => updateSetting('enable_high_cost_alerts', e.target.checked)}
                style={checkboxStyle}
              />
              <label htmlFor="enable_cost" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                💰 High Cost Alerts
              </label>
            </div>
            
            {settings?.enable_high_cost_alerts && (
              <div>
                <label style={labelStyle}>Alert when cost exceeds ($)</label>
                <input
                  type="number"
                  min="100"
                  max="100000"
                  step="100"
                  value={settings?.high_cost_threshold ?? 5000}
                  onChange={(e) => updateSetting('high_cost_threshold', parseFloat(e.target.value))}
                  style={inputStyle}
                  data-testid="cost-threshold"
                />
              </div>
            )}
          </div>

          {/* Servicing Alerts */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
              <input
                type="checkbox"
                id="enable_servicing"
                checked={settings?.enable_servicing_alerts ?? true}
                onChange={(e) => updateSetting('enable_servicing_alerts', e.target.checked)}
                style={checkboxStyle}
              />
              <label htmlFor="enable_servicing" style={{ fontWeight: '700', color: '#003366', fontSize: '1rem', cursor: 'pointer' }}>
                🛠️ Servicing Alerts
              </label>
            </div>
            
            {settings?.enable_servicing_alerts && (
              <div>
                <label style={labelStyle}>Show "due soon" alert when within (days)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={settings?.servicing_due_soon_days ?? 14}
                  onChange={(e) => updateSetting('servicing_due_soon_days', parseInt(e.target.value))}
                  style={inputStyle}
                  data-testid="servicing-days"
                />
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#64748B' }}>
                  Overdue vehicles will always show as high-priority alerts
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                color: '#64748B'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #003366 0%, #004d99 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontWeight: '600',
                color: 'white',
                opacity: saving ? 0.7 : 1
              }}
              data-testid="save-settings-btn"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AlertSettings;
